import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Box,
  Card
} from "@mui/material";
import { auth, getFirebaseErrorMessage } from "../../../firebase/firebase"; // Adjust path if needed
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { createUserWithEmailAndPassword } from "firebase/auth";

export const SignUpForm = ({ strings, defaultEmail = "", emailDisabled }) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);  
  const [email, setEmail] = useState(defaultEmail);

  useEffect(() => {
    if (defaultEmail) setEmail(defaultEmail);
  }, [defaultEmail]);


  const handleSignUp = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setConfirmPasswordError(true);
      return null;
    }
    setLoading(true);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setError(getFirebaseErrorMessage(error?.code));
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const googleBlue = "#1a73e8";

  return (
    <Box >
    <Card 
    sx={{
      width: '100%',
      maxWidth: '960px',
      borderRadius: 2,
      boxShadow: "0 1px 3px rgba(60,64,67,.30), 0 4px 8px rgba(60,64,67,.15)",
      overflow: 'hidden',
      minHeight: '1vh'
    }}>
    <Box
      component="form"
      onSubmit={handleSignUp}
      sx={{ px: { xs: 3, md: 6 }, pb: { xs: 4, md: 6 }, pt: { xs: 2, md: 0 }, maxWidth: '960px', width: '100%' }}
    >
      <Grid container spacing={{ xs: 3, md: 4 }}>
        <Grid item xs={12} md={5}>
          <Box sx={{ pr: { md: 6 }, mt: 2 }}>
            <Box
              component="img"
              src="/ct_logo_black.png"
              alt="CandidateTools logo"
              sx={{ width: '100%', maxWidth: '310px', height: 'auto', mb: 2, ml: -1 }}
            />
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 400, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
              {strings.signUpHeader}
            </Typography>
            <Typography variant="body1">
              {strings.signUpSubtitle}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box
            sx={{
              maxWidth: 420,
              ml: { md: "auto" },
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              mt: 2,
            }}
          >
            {/* Form Fields */}
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={strings.emailLabel}
              name="email"
              autoComplete="email"
              value={defaultEmail}
              disabled={emailDisabled}
              InputLabelProps={{ shrink: !!defaultEmail }}
            />
            <TextField
              label={strings.passwordLabel}
              type={showPassword ? "text" : "password"}
              fullWidth
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={strings.confirmPasswordLabel}
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              required
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setConfirmPasswordError(e.target.value !== password);
              }}
              error={confirmPasswordError}
              helperText={confirmPasswordError ? strings.passwordsMustMatch : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword((v) => !v)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError("")}>{error}</Alert>}
            
            {/* Form Actions */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                justifyContent: "flex-end",
                alignItems: "center",
                gap: { xs: 2, sm: 3 },
                mt: { xs: 2, md: 3 }
              }}
            >
              <Link component="button" type="button" variant="body2" sx={{ color: googleBlue }} onClick={() => navigate("/signin")}>
                {strings.switchToSignIn}
              </Link>
              <Button type="submit" variant="contained" sx={{ bgcolor: googleBlue }} disabled={loading || confirmPasswordError}>
                {loading ? <CircularProgress size={24} color="inherit" /> : strings.submitButton}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
    </Card>
    </Box>
  );
};